import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { AdSlot } from "@/components/AdSlot";
import { track } from "@/components/layernav/navConfig";
import { SectorHero } from "@/components/sector/SectorHero";
import { SectorMarketSnapshot, SectorLayerIndex } from "@/components/sector/SectorMarketPanel";
import { CryptoFrontLayer } from "@/components/sector/CryptoFrontLayer";
import { TrendingStrip } from "@/components/sector/TrendingStrip";
import { MarketIntelligence } from "@/components/sector/MarketIntelligence";
import { CryptoIntelligence } from "@/components/sector/CryptoIntelligence";
import { LatestCrypto, OriginalsSection, ResearchSection, ExplainersRail, RegulationSection } from "@/components/sector/SectorSections";
import { SectorClose } from "@/components/sector/SectorClose";

const BREADCRUMB_LD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "NewsLayer", item: "https://newslayer.com/" },
    { "@type": "ListItem", position: 2, name: "Crypto", item: "https://newslayer.com/crypto" },
  ],
};

export default function CryptoSectorPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["sector-crypto"],
    queryFn: async () => (await api.get("/sectors/crypto/overview")).data,
    refetchInterval: 120000,
    staleTime: 60000,
  });
  const { data: lower } = useQuery({
    queryKey: ["sector-crypto-lower"],
    queryFn: async () => (await api.get("/sectors/crypto/lower")).data,
    staleTime: 3 * 60 * 1000,
  });

  useEffect(() => {
    track("sector_page_view", { sector: "crypto" });
  }, []);

  return (
    <div data-testid="crypto-sector-page" className="pb-16">
      <Seo title="Crypto News, Prices & Market Intelligence — NewsLayer"
        description="Digital assets, markets, infrastructure and the forces shaping the crypto economy. Live prices, the Layer Index and NewsLayer's crypto intelligence in one command surface."
        path="/crypto" jsonLd={BREADCRUMB_LD} />
      <SectorHero data={data} />
      {isLoading ? (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-8" aria-hidden>
          <div className="h-[340px] rounded-xl border border-white/[0.06] bg-white/[0.02] animate-pulse" />
        </div>
      ) : (
        <CryptoFrontLayer lead={data?.lead || null} secondary={data?.secondary || []} />
      )}
      {/* §59 mobile/tablet recomposition: market modules follow the editorial front layer below lg */}
      <div className="lg:hidden max-w-[1400px] mx-auto px-4 sm:px-6 pt-6">
        <div className="grid md:grid-cols-2 gap-4">
          <SectorMarketSnapshot testid="sector-market-snapshot-mobile" />
          <SectorLayerIndex testid="sector-layer-index-mobile" />
        </div>
      </div>

      <TrendingStrip items={lower?.trending_strip || []} />
      <MarketIntelligence />
      <CryptoIntelligence intel={lower?.intelligence} />
      <LatestCrypto items={lower?.latest || []} />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10">
        <AdSlot placement="sector_mid" />
      </div>
      <OriginalsSection items={lower?.originals || []} />
      <ResearchSection items={lower?.research || []} />
      <ExplainersRail items={lower?.explainers || []} />
      <RegulationSection items={lower?.regulation || []} />
      <SectorClose />
    </div>
  );
}
